// ===========================================
// Settings → Onboarding → Registration fee credit (#374 Phase 3)
// ===========================================
// The school's switch for crediting a paid registration fee on the child's first invoice, and the
// scope ("one fee per family" or "per child"). OWNER-ONLY to change (the server's `ownerOnly`, here
// `isOwner`); other roles with `registration_fees.read` see it read-only; anyone else sees nothing.
//   - Turning ON asks which fees to credit (D6): every fee already paid (with the count) or only
//     fees paid from today. The answer is stored by the server as `creditFeesPaidFrom`.
//   - Turning OFF warns with the number of Held fees: they read "Paid" afterwards, and credits
//     already on invoices stay.
// Saves go straight to the server (their own PUT, not part of the onboarding form).

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import {
  useRegistrationFeePreview,
  useRegistrationFeeSettings,
  useUpdateRegistrationFeeSettings,
} from '../../../hooks/useRegistrationFeeSettings';

/** "2026-10-31" → "31/10/2026" without a Date (the value is a school day, not an instant). */
function dayKeyToDisplay(key: string): string {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
}

/** Whole-number text -> int, or null when it is not a clean integer in range. */
function intInRange(text: string, min: number, max: number): number | null {
  if (!/^[0-9]+$/.test(text.trim())) return null;
  const n = Number(text);
  return n >= min && n <= max ? n : null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

interface ClockProps {
  limitMonths: number;
  warnDays: number;
  isOwner: boolean;
  saving: boolean;
  onSave: (patch: { waitlistLimitMonths: number; waitlistWarnDays: number }) => void;
}

/** The Meet & Greet waitlist clock (#374 release B): how long a family may wait, and when to warn. */
function WaitlistClockFields({ limitMonths, warnDays, isOwner, saving, onSave }: ClockProps) {
  const [limit, setLimit] = useState(String(limitMonths));
  const [warn, setWarn] = useState(String(warnDays));
  useEffect(() => {
    setLimit(String(limitMonths));
    setWarn(String(warnDays));
  }, [limitMonths, warnDays]);
  const limitValue = intInRange(limit, 1, 24);
  const warnValue = intInRange(warn, 0, 90);
  const changed = limit.trim() !== String(limitMonths) || warn.trim() !== String(warnDays);
  const valid = limitValue !== null && warnValue !== null;
  return (
    <Box sx={{ mt: 3, ml: { sm: 6 }, minWidth: 0 }} data-testid="waitlist-clock-fields">
      <Typography variant="subtitle2">Meet & Greet waitlist</Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
        <TextField
          size="small"
          label="Waitlist limit (months)"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          disabled={!isOwner || saving}
          error={limitValue === null}
          helperText={limitValue === null ? 'Whole months, 1 to 24' : undefined}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ width: { xs: '100%', sm: 220 } }}
        />
        <TextField
          size="small"
          label="Warn this many days before"
          value={warn}
          onChange={(e) => setWarn(e.target.value)}
          disabled={!isOwner || saving}
          error={warnValue === null}
          helperText={warnValue === null ? 'Whole days, 0 to 90' : undefined}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ width: { xs: '100%', sm: 220 } }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        A new limit applies to bookings put on the waitlist from then on; a new warning applies at once.
      </Typography>
      {isOwner && (
        <Button
          variant="contained"
          size="small"
          sx={{ mt: 1, minHeight: { xs: 44 } }}
          disabled={!changed || !valid || saving}
          onClick={() => valid && onSave({ waitlistLimitMonths: limitValue, waitlistWarnDays: warnValue })}
        >
          Save waitlist settings
        </Button>
      )}
    </Box>
  );
}

export default function RegistrationFeeCreditCard() {
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const mayRead = can('registration_fees', 'read');
  const settings = useRegistrationFeeSettings(mayRead);
  const preview = useRegistrationFeePreview(isOwner && mayRead);
  const update = useUpdateRegistrationFeeSettings();
  const [dialog, setDialog] = useState<'ON' | 'OFF' | null>(null);
  const [creditFrom, setCreditFrom] = useState<'ALL' | 'TODAY'>('ALL');

  if (!mayRead) return null;

  if (settings.isLoading) return <Skeleton variant="rounded" height={140} />;
  if (settings.isError || !settings.data) {
    return <Alert severity="error">The registration fee credit settings could not be loaded.</Alert>;
  }

  const s = settings.data;
  const heldNow = preview.data?.heldNow;
  const everyPaid = preview.data?.everyPaidFee;

  const confirm = () => {
    if (dialog === 'ON') {
      update.mutate({ creditOnFirstInvoice: true, creditFrom }, { onSuccess: () => setDialog(null) });
    } else if (dialog === 'OFF') {
      update.mutate({ creditOnFirstInvoice: false }, { onSuccess: () => setDialog(null) });
    }
  };

  return (
    <Card variant="outlined" data-testid="registration-fee-credit-card">
      <CardHeader
        title="Registration fee credit"
        subheader="Take a paid registration fee off the child's first invoice"
      />
      <CardContent>
        <FormControlLabel
          control={
            <Switch
              checked={s.creditOnFirstInvoice}
              disabled={!isOwner || update.isPending}
              onChange={(_, checked) => {
                setCreditFrom('ALL');
                setDialog(checked ? 'ON' : 'OFF');
              }}
              inputProps={{ 'aria-label': "Credit the registration fee on the child's first invoice" }}
            />
          }
          label={
            <Box>
              <Typography variant="body1">Credit the registration fee on the child&apos;s first invoice</Typography>
              <Typography variant="body2" color="text.secondary">
                A credit off that child&apos;s tuition, not a refund. Off: the school keeps the fee.
              </Typography>
            </Box>
          }
        />
        {s.creditOnFirstInvoice && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: 0.5 }}>
            {s.creditFeesPaidFrom
              ? `Credits fees paid from ${dayKeyToDisplay(s.creditFeesPaidFrom)}.`
              : 'Credits every fee already paid.'}
          </Typography>
        )}

        <FormControl sx={{ mt: 2, ml: 6 }} disabled={!isOwner || update.isPending}>
          <FormLabel id="registration-fee-scope-label">How many fees a family pays</FormLabel>
          <RadioGroup
            aria-labelledby="registration-fee-scope-label"
            value={s.scope}
            onChange={(_, value) => update.mutate({ scope: value as 'PER_FAMILY' | 'PER_STUDENT' })}
          >
            <FormControlLabel value="PER_FAMILY" control={<Radio size="small" />} label="One fee per family" />
            <FormControlLabel value="PER_STUDENT" control={<Radio size="small" />} label="One fee per child" />
          </RadioGroup>
        </FormControl>

        <WaitlistClockFields
          limitMonths={s.waitlistLimitMonths}
          warnDays={s.waitlistWarnDays}
          isOwner={isOwner}
          saving={update.isPending}
          onSave={(patch) => update.mutate(patch)}
        />

        {!isOwner && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Only the {ownerTitle} can change this.
          </Alert>
        )}
      </CardContent>

      <Dialog open={dialog !== null} onClose={() => !update.isPending && setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog === 'ON' ? 'Credit registration fees on the first invoice' : 'Stop crediting registration fees'}
        </DialogTitle>
        <DialogContent>
          {dialog === 'ON' && (
            <FormControl>
              <FormLabel id="registration-fee-from-label">Which fees get the credit?</FormLabel>
              <RadioGroup
                aria-labelledby="registration-fee-from-label"
                value={creditFrom}
                onChange={(_, value) => setCreditFrom(value as 'ALL' | 'TODAY')}
              >
                <FormControlLabel
                  value="ALL"
                  control={<Radio size="small" />}
                  label={
                    everyPaid === undefined
                      ? 'Every fee already paid'
                      : `Every fee already paid (${plural(everyPaid, 'child', 'children')})`
                  }
                />
                <FormControlLabel value="TODAY" control={<Radio size="small" />} label="Only fees paid from today" />
              </RadioGroup>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Each credited child gets the fee off their next invoice. Check Reports → Registration fees not yet
                applied before you generate invoices.
              </Typography>
            </FormControl>
          )}
          {dialog === 'OFF' && (
            <Alert severity="warning">
              {heldNow !== undefined && heldNow > 0
                ? `${plural(heldNow, 'paid fee is', 'paid fees are')} waiting to be credited. They will stop being credited and read "Paid". `
                : 'Paid fees will read "Paid" and stop being credited. '}
              Credits already on invoices stay as they are.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={update.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirm} disabled={update.isPending}>
            {dialog === 'ON' ? 'Switch on' : 'Switch off'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
